import type { Card, WildCard, WildKind } from "../types/card";
import { HALF_UNIT_SUITS, MAX_VALUE, MIN_VALUE, SUITS, WILD_KINDS } from "../types/card";
import type { RNG } from "./rng";

/**
 * Deck sizes are expressed in half-deck increments, 0.5 through 4.0. A deck is
 * assembled from "units":
 *
 *   full unit (1.0) — 4 copies of each value 1-13 (52 cards), plus ONE of each
 *                     enabled wild kind.
 *   half unit (0.5) — 2 copies of each value 1-13 (26 cards), plus ONE each of
 *                     `HALF_UNIT_WILDS` (two) enabled kinds, drawn at random at
 *                     every deal (see `pickHalfUnitWildKinds`).
 *
 * So 1.5 decks is one full unit plus one half unit, 2.5 is two full plus a half,
 * and so on. Keep this in sync with services/game-server/internal/engine/deck.go
 * and the "Setup" section of DESIGN_raw.md.
 *
 * **A wild is rarer than the values it sits among, and deliberately.** One per
 * kind per full unit means a full deck is 52 + 4 rather than 52 + 8: a wild is
 * something a player waits for, and at two apiece the drama of one landing had
 * worn off by the middle of the first round.
 *
 * **The half unit cannot afford four.** It is half a full unit's value cards
 * but would contribute as many wild KINDS as one, which made every odd size
 * wild-heavy. Two is the share that keeps a half unit in line with a full one.
 * WHICH two is drawn per round, from whatever the host left on, so a host never
 * has to choose between kinds to play a half deck — they leave everything on and
 * the deal decides. With two or fewer on there is nothing to choose and the
 * generator is not touched.
 */
export const DECK_SIZES = [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4] as const;
export type DeckSize = (typeof DECK_SIZES)[number];

/**
 * Which wild kinds are shuffled into the pool. Any combination is legal from one
 * size, including all off — see `drainsCards` for the combination worth
 * warning about. A half unit carries `HALF_UNIT_WILDS` of the kinds that are on,
 * picked at the deal.
 */
export interface WildConfig {
  smash: boolean;
  lightning: boolean;
  swap: boolean;
  peek: boolean;
}

export interface DeckConfig {
  size: DeckSize;
  wilds: WildConfig;
}

export const DEFAULT_DECK_CONFIG: DeckConfig = {
  size: 0.5,
  // Every kind on: a host switches off what they do not want, each time they set
  // a table up, rather than having to remember to switch kinds on.
  wilds: { smash: true, lightning: true, swap: true, peek: true },
};

/** Every player must be dealt at least this many cards, or the round is over before it starts. */
export const MIN_CARDS_PER_PLAYER = 5;

export function isDeckSize(value: number): value is DeckSize {
  return (DECK_SIZES as readonly number[]).includes(value);
}

/** True when the size includes a trailing half unit (0.5, 1.5, 2.5). */
export function hasHalfUnit(size: DeckSize): boolean {
  return size % 1 !== 0;
}

export function fullUnitCount(size: DeckSize): number {
  return Math.floor(size);
}

/** How many copies of each value 1-13 the assembled pool contains. */
export function copiesPerValue(size: DeckSize): number {
  return fullUnitCount(size) * 4 + (hasHalfUnit(size) ? 2 : 0);
}

/**
 * Repairs a wild config's SHAPE: every kind present, every value a boolean.
 *
 * That is all it does now. It used to enforce the half deck's one-wild limit,
 * which no longer exists; what is left is the other job it was always quietly
 * doing — standing between the engine and configs written before a kind
 * existed. Saved local setups and old Firestore documents carry only `smash` and
 * `lightning`, and a missing key has to read as OFF rather than as undefined.
 * Mirrors `NormalizeWilds` in the Go engine.
 */
export function normalizeWilds(wilds: Partial<Record<WildKind, unknown>> | null | undefined): WildConfig {
  const on = (kind: WildKind) => wilds?.[kind] === true;
  return { smash: on("smash"), lightning: on("lightning"), swap: on("swap"), peek: on("peek") };
}

/** The enabled kinds, in `WILD_KINDS` order. */
export function enabledWildKinds(wilds: WildConfig): WildKind[] {
  const normalized = normalizeWilds(wilds);
  return WILD_KINDS.filter((kind) => normalized[kind]);
}

/** How many wild cards (of distinct kinds) a half unit contributes. */
export const HALF_UNIT_WILDS = 2;

/** A half deck is the one size with no full unit at all. */
export function isHalfDeck(size: DeckSize): boolean {
  return size === 0.5;
}

/**
 * True when a half unit has more kinds on than it has room for, so which ones it
 * carries is drawn at the deal rather than known in advance. Screens use it to
 * say "picked at random" instead of naming the kinds.
 */
export function halfUnitPicksAtRandom(wilds: WildConfig): boolean {
  return enabledWildKinds(wilds).length > HALF_UNIT_WILDS;
}

/**
 * The kinds a half unit carries this deal, in `WILD_KINDS` order.
 *
 * Two or fewer on: all of them, and the generator is NOT drawn from — so every
 * seeded deal that predates this rule is the deal it always was. More: two
 * distinct kinds, drawn from the enabled ones. Returned in `WILD_KINDS` order
 * rather than draw order, so the pool a seed shuffles depends on which kinds
 * were drawn and nothing else. Mirrors `pickHalfUnitWildKinds` in the Go engine.
 */
export function pickHalfUnitWildKinds(wilds: WildConfig, rng: RNG): WildKind[] {
  const pool = enabledWildKinds(wilds);
  if (pool.length <= HALF_UNIT_WILDS) return pool;
  const picked = new Set<WildKind>();
  for (let i = 0; i < HALF_UNIT_WILDS; i++) {
    const [kind] = pool.splice(rng.nextInt(pool.length), 1);
    picked.add(kind);
  }
  return WILD_KINDS.filter((kind) => picked.has(kind));
}

/**
 * Repairs a config's shape for the engine. Every size holds every kind the host
 * switched on now — a half unit picks from them at the deal — so there is
 * nothing to trim; this is `normalizeWilds` with the size carried along, kept as
 * the one named step every host-supplied config passes through. Mirrors
 * `NormalizeDeckConfig`.
 */
export function normalizeDeckConfig(config: DeckConfig): DeckConfig {
  return { size: config.size, wilds: normalizeWilds(config.wilds) };
}

/**
 * Does anything in this pool take cards OUT of play?
 *
 * Smash and Lightning are the only things in the game that shrink the table —
 * Swap and Peek move and reveal cards but never remove one. A table without
 * either still ends, but every taken pile goes back into somebody's deck, so
 * rounds run far longer; the setup screens use this to say so. Mirrors
 * `DrainsCards` in the Go engine.
 */
export function drainsCards(wilds: WildConfig): boolean {
  const normalized = normalizeWilds(wilds);
  return normalized.smash || normalized.lightning;
}

/** How many wild cards the assembled pool contains, given the size and toggles. */
export function wildCount(config: DeckConfig): number {
  const enabled = enabledWildKinds(config.wilds).length;
  return fullUnitCount(config.size) * enabled + (hasHalfUnit(config.size) ? Math.min(HALF_UNIT_WILDS, enabled) : 0);
}

function wildCard(id: string, kind: WildKind, deckIndex: number): WildCard {
  return { id, kind: "wild", wildIndex: WILD_KINDS.indexOf(kind) as WildCard["wildIndex"], wildKind: kind, deckIndex };
}

/** Total cards in the pool for this configuration. */
export function totalCardCount(config: DeckConfig): number {
  return copiesPerValue(config.size) * (MAX_VALUE - MIN_VALUE + 1) + wildCount(config);
}

/**
 * One full 52-card value block plus ONE of each enabled wild.
 *
 * The copy index doubles as the suit index, so the four copies of a value are
 * one of each suit. Card ids keep the old `d0-v5-2` shape rather than naming the
 * suit — they are identity for animations and React keys, and copy-to-suit is a
 * fixed 1:1 mapping, so there is nothing to gain from churning them.
 *
 * Wild ids keep their `d{deck}-w{kindIndex}-{copy}` shape too, with the copy
 * index now always 0: it is there to keep a unit's copies of a kind apart, and a
 * unit holding one still wants ids shaped like every other unit's. The Go engine
 * builds the same ids in the same order.
 */
function buildFullUnit(deckIndex: number, wilds: WildConfig): Card[] {
  const cards: Card[] = [];
  for (let value = MIN_VALUE; value <= MAX_VALUE; value++) {
    for (let copy = 0; copy < 4; copy++) {
      cards.push({ id: `d${deckIndex}-v${value}-${copy}`, kind: "value", value, suit: SUITS[copy], deckIndex });
    }
  }
  for (const kind of enabledWildKinds(wilds)) {
    cards.push(wildCard(`d${deckIndex}-w${WILD_KINDS.indexOf(kind)}-0`, kind, deckIndex));
  }
  return cards;
}

/**
 * Half of a deck: 2 copies of each value in HALF_UNIT_SUITS, plus one each of the
 * kinds drawn for it (`h{deck}-w{kindIndex}`), already in `WILD_KINDS` order.
 */
function buildHalfUnit(deckIndex: number, kinds: WildKind[]): Card[] {
  const cards: Card[] = [];
  for (let value = MIN_VALUE; value <= MAX_VALUE; value++) {
    for (let copy = 0; copy < 2; copy++) {
      cards.push({ id: `h${deckIndex}-v${value}-${copy}`, kind: "value", value, suit: HALF_UNIT_SUITS[copy], deckIndex });
    }
  }
  for (const kind of kinds) {
    cards.push(wildCard(`h${deckIndex}-w${WILD_KINDS.indexOf(kind)}`, kind, deckIndex));
  }
  return cards;
}

/**
 * Builds the full pool of cards for a deck configuration. Takes the round's
 * generator because a half unit's wilds are drawn here (and only when there is a
 * choice to make — see `pickHalfUnitWildKinds`).
 */
export function buildDeckPool(config: DeckConfig, rng: RNG): Card[] {
  const { wilds } = normalizeDeckConfig(config);
  const cards: Card[] = [];
  const fulls = fullUnitCount(config.size);
  for (let i = 0; i < fulls; i++) cards.push(...buildFullUnit(i, wilds));
  if (hasHalfUnit(config.size)) cards.push(...buildHalfUnit(fulls, pickHalfUnitWildKinds(wilds, rng)));
  return cards;
}

/** Cards available to deal to players, after the 5 face-up table cards are taken. */
export function dealableCardCount(config: DeckConfig): number {
  return Math.max(0, totalCardCount(config) - 5);
}

/** How many cards each player would start with under this configuration. */
export function cardsPerPlayer(config: DeckConfig, playerCount: number): number {
  if (playerCount <= 0) return 0;
  return Math.floor(dealableCardCount(config) / playerCount);
}

/** True when every player gets at least MIN_CARDS_PER_PLAYER cards. */
export function supportsPlayerCount(config: DeckConfig, playerCount: number): boolean {
  return cardsPerPlayer(config, playerCount) >= MIN_CARDS_PER_PLAYER;
}

/**
 * The smallest deck size that can seat `playerCount` players with at least
 * MIN_CARDS_PER_PLAYER cards each, given the wild toggles. Returns the largest
 * size if even that is not enough (only possible with absurd player counts).
 */
export function minDeckSizeForPlayerCount(playerCount: number, wilds: WildConfig): DeckSize {
  for (const size of DECK_SIZES) {
    if (supportsPlayerCount({ size, wilds }, playerCount)) return size;
  }
  return DECK_SIZES[DECK_SIZES.length - 1];
}

/** Human-readable label used across the setup screens, leaderboards, and stat keys. */
export function deckSizeLabel(size: DeckSize): string {
  if (size === 0.5) return "Half deck";
  if (size === 1) return "1 deck";
  return `${size} decks`;
}

/** Stable key for stats/high-score storage, e.g. "0.5", "1", "1.5". */
export function deckSizeKey(size: DeckSize): string {
  return String(size);
}

const WILD_NAMES: Record<WildKind, string> = { smash: "Smash", lightning: "Lightning", swap: "Swap", peek: "Peek" };

export function describeDeckConfig(config: DeckConfig): string {
  const on = enabledWildKinds(config.wilds).map((kind) => WILD_NAMES[kind]);
  return `${deckSizeLabel(config.size)} · ${on.length ? `${on.join(" + ")} wilds` : "no wilds"}`;
}
